import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RouterUtil } from '@seahorse/common';
import { OrganisationModel, OrganisationsDataService } from '@seahorse/domain';
import {
  DialogService,
  PageAction,
  PageHeaderComponent,
  ShTableComponent,
} from '@seahorse/shell';
import type { ShColumn, ShTablePaging, ShTableQuery } from '@seahorse/shell';
import { Subscription } from 'rxjs';
import { SeahorseModuleFormComponent } from '../../components/seahorse-module-form/seahorse-module-form.component';

/**
 * The Seahorse modules overview: the organisations (searchable, paged) and,
 * beside the one picked, its modules (the child route). "New module" opens
 * the add dialog.
 */
@Component({
  selector: 'su-seahorse-module-overview',
  templateUrl: './seahorse-module-overview.page.html',
  imports: [
    CommonModule,
    TranslateModule,
    RouterModule,
    PageHeaderComponent,
    ShTableComponent,
  ],
})
export class SeahorseModuleOverviewPage implements OnInit, OnDestroy {
  isLoading = false;
  pageIndex = 0;
  readonly pageSize = 20;
  search = '';
  resultCount: number | null = null;

  organisations: OrganisationModel[] = [];
  selectedOrganisation: OrganisationModel = null;

  // Stable fields: they feed the page header and sh-table.
  paging: ShTablePaging = { mode: 'server', pageSize: this.pageSize, total: 0 };
  readonly columns: ShColumn<OrganisationModel>[] = [
    { key: 'name', header: 'organisation.all' },
  ];
  readonly primaryAction: PageAction = {
    label: 'shared.terms.new',
    command: () => this.addSeahorseModule(),
  };

  private _subscriptions = new Subscription();

  private _routerUtil = inject(RouterUtil);
  private _accountDataService = inject(OrganisationsDataService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _dialogService = inject(DialogService);
  private _translateService = inject(TranslateService);

  ngOnInit(): void {
    this._translateService
      .get('seahorseModules.seahorseModules')
      .subscribe((result) => {
        this._routerUtil.currentModuleName = result;
      });

    const organisationId =
      this._route.snapshot.firstChild?.params['organisationId'];

    if (organisationId) {
      this._router.navigate(['../overview'], {
        relativeTo: this._route,
      });
    }

    this.loadOrganisations();
  }

  loadOrganisations() {
    this.isLoading = true;

    this._subscriptions.add(
      this._accountDataService
        .searchOrganisations(
          this.search,
          this.pageIndex,
          this.pageSize,
          false,
          'Name'
        )
        .subscribe({
          next: (result) => {
            this.resultCount = result.count;
            this.organisations = result.result;
            this.paging = {
              mode: 'server',
              pageSize: this.pageSize,
              total: result.count,
            };
          },
          complete: () => {
            this.isLoading = false;
          },
        })
    );
  }

  onSearchChange(search: string) {
    this.search = search ?? '';
    this.pageIndex = 0;
    this.loadOrganisations();
  }

  onQueryChange(query: ShTableQuery) {
    this.pageIndex = query.page;
    this.loadOrganisations();
  }

  selectOrganisation(organisation: OrganisationModel) {
    this.selectedOrganisation = organisation;

    if (organisation != null) {
      this._router.navigate(['/seahorse-modules/overview', organisation.id], {
        state: { organisationName: organisation.name },
      });
    } else {
      this._router.navigate(['/seahorse-modules/overview']);
    }
  }

  addSeahorseModule() {
    this._dialogService.open(SeahorseModuleFormComponent, {
      title: 'seahorseModules.wizard.addModule',
      width: 'lg',
    });
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
